import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { Box, Typography, useTheme, CircularProgress } from "@mui/material";
import { FoodsIcon } from "@foodpilot/foods";
import { CompanyHierarchyNode } from "src/api/client-api/company/useOrganizationHierarchy";
import { Company } from "src/api/client-api/useUserNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useEffect, useMemo, useState } from "react";
import { buildCompanyTreeNodes, type CompanyTreeNode, normalizeCompanyHierarchy } from "./companyTreeUtils";

type CompanyTreeViewProps = {
    companies: CompanyHierarchyNode[] | undefined;
    selectedCompany: Company;
    onSelect: (company: CompanyHierarchyNode) => void;
    selectableIds: Set<number>;
    isLoading?: boolean;
};

export const CompanyTreeView = (props: CompanyTreeViewProps) => {
    const { companies, selectedCompany, onSelect, isLoading, selectableIds } = props;
    const theme = useTheme();

    const selectedColor = theme.palette.primary[600] ?? theme.palette.primary.main;

    const treeNodes = useMemo(() => {
        if (!companies || companies.length === 0) {
            return [];
        }

        return buildCompanyTreeNodes(normalizeCompanyHierarchy(companies));
    }, [companies]);

    const selectedAncestorIds = useMemo(() => {
        if (treeNodes.length === 0) {
            return [];
        }

        return findAncestorItemIds(treeNodes, selectedCompany.id);
    }, [treeNodes, selectedCompany.id]);

    const selectedItemId = useMemo(() => {
        if (treeNodes.length === 0) {
            return null;
        }

        return findFirstTreeItemId(treeNodes, selectedCompany.id);
    }, [treeNodes, selectedCompany.id]);

    const [expandedItems, setExpandedItems] = useState<string[]>(selectedAncestorIds);

    useEffect(() => {
        setExpandedItems((previous) => {
            return Array.from(new Set([...previous, ...selectedAncestorIds]));
        });
    }, [selectedAncestorIds]);

    if (isLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (treeNodes.length === 0) {
        return null;
    }

    const renderTree = (nodes: CompanyTreeNode[]) => {
        return nodes.map((node) => {
            const isSelected = node.company.id === selectedCompany.id;
            const isSelectable = !selectableIds || selectableIds.has(node.company.id);

            return (
                <TreeItem
                    key={node.treeItemId}
                    itemId={node.treeItemId}
                    label={
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                                py: 0.5,
                                pr: 1,
                                cursor: isSelectable ? "pointer" : "default",
                            }}
                            onClick={() => {
                                if (isSelectable) {
                                    onSelect(node.company);
                                }
                            }}
                        >
                            <Typography
                                variant="body-bold"
                                sx={{
                                    color:
                                        !isSelectable ? "text.disabled"
                                        : isSelected ? selectedColor
                                        : "inherit",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {node.company.name}
                            </Typography>
                            {isSelected && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        width: "16px",
                                        height: "16px",
                                        ml: 1,
                                        color: selectedColor,
                                    }}
                                >
                                    <FoodsIcon icon={"check"} size={2} />
                                </Box>
                            )}
                        </Box>
                    }
                >
                    {node.children.length > 0 ? renderTree(node.children) : null}
                </TreeItem>
            );
        });
    };

    return (
        <Box
            sx={{
                minWidth: "287px",
                margin: "20px 0",
                "& .MuiTreeItem-content": {
                    borderRadius: 0,
                },
                "& .MuiTreeItem-root .MuiTreeItem-content.Mui-selected": {
                    backgroundColor: "transparent",
                },
                "& .MuiTreeItem-root .MuiTreeItem-content.Mui-selected:hover": {
                    backgroundColor: "transparent",
                },
            }}
        >
            <SimpleTreeView
                selectedItems={selectedItemId ?? undefined}
                expandedItems={expandedItems}
                onExpandedItemsChange={(_event, itemIds) => {
                    setExpandedItems(Array.from(new Set([...itemIds, ...selectedAncestorIds])));
                }}
                slots={{
                    collapseIcon: ExpandMoreIcon,
                    expandIcon: ChevronRightIcon,
                }}
            >
                {renderTree(treeNodes)}
            </SimpleTreeView>
        </Box>
    );
};

const findAncestorItemIds = (nodes: CompanyTreeNode[], targetId: number): string[] => {
    const ancestorIds = new Set<string>();

    const traverse = (currentNodes: CompanyTreeNode[], path: string[]) => {
        for (const node of currentNodes) {
            const currentPath = [...path, node.treeItemId];

            if (node.company.id === targetId) {
                for (const ancestorId of path) {
                    ancestorIds.add(ancestorId);
                }
            }

            if (node.children.length === 0) {
                continue;
            }

            traverse(node.children, currentPath);
        }
    };

    traverse(nodes, []);

    return Array.from(ancestorIds);
};

const findFirstTreeItemId = (nodes: CompanyTreeNode[], targetId: number): string | null => {
    for (const node of nodes) {
        if (node.company.id === targetId) {
            return node.treeItemId;
        }

        if (node.children.length === 0) {
            continue;
        }

        const found = findFirstTreeItemId(node.children, targetId);
        if (found !== null) {
            return found;
        }
    }

    return null;
};
